<template>
    <div id="app">
        <top v-if="!isPaymentIframe" />

        <router-view />

        <clinic-not-found />

        <bottom v-if="!isPaymentIframe" />
        <footer-component v-if="!isPaymentIframe" />
    </div>
</template>

<style lang="scss">
    @import '@/assets/styles/app.scss';
</style>

<script>
    import { mapActions, mapState } from 'vuex';

    import Place from './lib/Place';

    import FooterComponent from './components/Global/Footer.vue';
    import Bottom from './components/Global/Bottom';
    import Top from './components/Global/Top';
    import Clinic from "./lib/Clinic";
    import ClinicNotFound from "./components/Form/Modals/ClinicNotFound";

    export default {
        components: {
            Bottom,
            Top,
            ClinicNotFound,
            FooterComponent,
        },

        computed: {
            ...mapState('appointment', [
                'appointmentData',
            ]),

            ...mapState('google', [
                'googleLoaded',
            ]),
        },

        data() {
            const iframeRouteNames = [
                'secure-payment',
                'payment-successful',
                'payment-unsuccessful',
                'appointment-error',
            ];

            return {
                isHome: this.$route.name === 'home',
                isPaymentIframe: window._.includes(iframeRouteNames, this.$route.name),
                isAppointmentConfirmation: this.$route.name === 'appointment-confirmation',
            };
        },

        created() {
            // Inject additional script tags.
            this.addGoogleMapsLib();
            // this.addLiveChatLib();

            // Hide the loader (see /public/index.html).
            document.getElementById('loading').classList.add('hidden');

            // Listen for Google load events.
            this.listen('google.load', [this.setGoogleLoaded, Place.autoload]);
            this.listen('google.place.load', this.setPlaceAndId);

            // Autoload the place from the URL's query string.
            // Note: we run this manually now in case the event we're listening for above has already been triggered.
            Place.autoload();

            // Listen for a clinic to be loaded; we'll want to set the place automatically.
            this.listen('clinic.load', this.setPlaceUsingClinic);

            // Set the clinic ID from the URL's query string.
            this.listen('clinic.not-found', this.showClinicNotFoundModal);
            this.setClinicId(Clinic.getClinicId());

            // Initialize the appointment data and make sure the user is on the right page.
            this.initializeData();

            // Listen for messages from within the 3D Secure iframe.
            window.addEventListener('message', this.onMessageReceived, false);
        },

        destroyed() {
            this.silence('google.load', [this.setGoogleLoaded, Place.autoload]);
            this.silence('google.place.load', this.setPlaceAndId);
            this.silence('payment.success', this.onPaymentSuccess);
            this.silence('clinic.load', this.setPlaceUsingClinic);
            this.silence('clinic.not-found', this.showClinicNotFoundModal);

            window.removeEventListener('message', this.onMessageReceived, false);
        },

        methods: {
            ...mapActions('google', [
                'setGoogleLoaded',
            ]),

            ...mapActions('appointment', [
                'setRawAppointmentData',
                'setClinicId',
                'setPlace',
                'setPlaceId',
            ]),

            ...mapActions('voucher', [
                'clearVoucherCode',
            ]),

            /**
             * Load the Google Maps library.
             */
            addGoogleMapsLib() {
                const script = document.createElement('script');

                script.type = 'text/javascript';
                script.async = true;
                script.defer = true;
                script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.VUE_APP_GOOGLE_API_KEY}&libraries=geometry,places&callback=googleLibsLoaded`;

                document.body.appendChild(script);
            },

            /**
             * Load the Live Chat library.
             */
            // addLiveChatLib() {
            //     const script = document.createElement('script');
            //
            //     script.type = 'text/javascript';
            //     script.async = true;
            //     script.defer = true;
            //     script.src = 'https://ascenti.ourcontactcentre.com/webchat/chat-uri.js';
            //     script.onload = () => window.Events.dispatch('chat.loaded');
            //
            //     document.body.appendChild(script);
            // },

            /**
             * Set the Google Place and ID in the vuex store.
             */
            setPlaceAndId(place) {
                this.setPlace(place);
                this.setPlaceId(place.place_id);
            },

            /**
             * Determine whether the user should be sent back to the booking page.
             */
            shouldSendToBooking() {
                return !this.isPaymentIframe && !this.isAppointmentConfirmation;
            },

            /**
             * Initialize the appointment data and make sure the user is on the right page.
             */
            initializeData() {
                // If we are on the homepage, clear the voucher code from the vuex store.
                if (this.isHome) {
                    this.clearVoucherCode();
                }

                // If we are not on the appointment confirmation screen, clean up the local storage.
                if (!this.isAppointmentConfirmation) {
                    window.localStorage.removeItem('appointment');
                }

                // The appointment data is auto-populated from a cookie. If the slot has expired, clear
                // the data; otherwise, send the user to the booking page to finish checking-out.
                if (this.appointmentData) {
                    const data = this.appointmentData;

                    try {
                        data.expires = window.moment(data.expires);

                        if (data.expires.isBefore(window.moment())) {
                            this.setRawAppointmentData(null);
                        } else {
                            this.setRawAppointmentData(data);

                            if (this.shouldSendToBooking()) {
                                this.$router.push({ name: 'booking' });
                            }
                        }
                    } catch (error) {
                        this.setRawAppointmentData(null);
                    }
                }
            },

            /**
             * Dispatch events when we receive messages from the 3D Secure iframe.
             */
            onMessageReceived(e) {
                const proxyMessagesToEvents = [
                    'payment.success',
                    'payment.error',
                    'appointment.error',
                ];

                if (window._.includes(proxyMessagesToEvents, e.data)) {
                    this.dispatch(e.data);
                }
            },

            setPlaceUsingClinic(clinic) {
                Place.loadPlaceUsingAddress(
                    clinic.ClinicName + ',' + clinic.Address1 + ',' + clinic.Postcode
                );
            },

            /**
             * A callback to show the "clinic not found" modal.
             *
             * Note: triggered by the getClinic action of the appointment vuex module.
             */
            showClinicNotFoundModal() {
                this.showModal('clinic-not-found');
            },
        },
    }
</script>
